import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import React from "react";

function getPageNumbers(current: number, total: number): (number | "...")[] {
  const delta = 2;
  const range = [];
  const rangeWithDots: (number | "...")[] = [];
  let l: number | undefined;

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (l !== undefined) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l > 2) {
        rangeWithDots.push("...");
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
}

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  page,
  pageSize,
  total,
  setPage,
  setPageSize,
  pageSizeOptions = [5, 10, 15, 20, 50, 100],
}) => {
  const totalPages = Math.ceil(total / pageSize);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return (
    <div className="flex flex-col lg:flex-row items-center justify-end md:justify-between pt-6 gap-2 lg:gap-0">
      {/* Left: Showing count */}
      <div className="text-sm text-muted-foreground hidden md:block">
        Showing {from}-{to} of {total}
      </div>

      {/* Right: Pagination and pageSize selector */}
      <div className="flex items-center space-x-4">
        {/* Page size selector */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground hidden md:block">Rows per page:</span>
          <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(Number(value))}>
            <SelectTrigger className="w-[72px] h-8">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pagination controls */}
        <div className="flex items-center space-x-1">
          <Button size="icon" variant="outline" disabled={page === 1} onClick={() => setPage(1)}>
            <ChevronsLeft className="w-4 h-4" />
          </Button>

          <Button size="icon" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>

          {getPageNumbers(page, totalPages).map((p, i) =>
            typeof p === "number" ? (
              <Button key={i} variant={p === page ? "default" : "outline"} size="sm" onClick={() => setPage(p)}>
                {p}
              </Button>
            ) : (
              <span key={i} className="px-2 text-muted-foreground text-sm">
                {p}
              </span>
            )
          )}

          <Button size="icon" variant="outline" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>

          <Button size="icon" variant="outline" disabled={page === totalPages} onClick={() => setPage(totalPages)}>
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
